import React, { useEffect, useState } from 'react'
import { TrashIcon, DrohneIcon } from '../../Icons'
import { Button, Input } from '../../StyledComponents'

export interface StationModelInfo {
    id: number
    key: string
    domain: string
    type: string
    subType: string
    name: string
    deviceClass: string
}

export interface StationCameraInfo {
    id: number
    cameraIndex: string
    cameraPosition: string
}

export interface DroneStationItem {
    uuid: string
    name: string
    created?: string
    updated?: string
    droneId?: number
    droneName?: string
    droneStationLatitude?: number
    droneStationLongitude?: number
    droneDiameter?: number
    serialNumber?: string
    callsign?: string
    deviceOnlineStatus?: boolean
    modeCode?: number
    stationModel?: StationModelInfo
    cameras?: StationCameraInfo[]
}

export interface DroneStationEditPayload {
    latitude: number
    longitude: number
}

interface DroneStationDropdownCardProps {
    station: DroneStationItem
    onDelete: (station: DroneStationItem) => void
    onEdit: (station: DroneStationItem, payload: DroneStationEditPayload) => void | Promise<void>
    expanded: boolean
    onToggle: (station: DroneStationItem) => void
}

function formatCoord(value?: number) {
    if (value == null || Number.isNaN(value)) return '-'
    return value.toFixed(4)
}

export default function DroneStationDropdownCard({
    station,
    onDelete,
    onEdit,
    expanded,
    onToggle,
}: DroneStationDropdownCardProps) {
    const [isEditing, setIsEditing] = useState(false)
    const [editLat, setEditLat] = useState(String(station.droneStationLatitude ?? ''))
    const [editLng, setEditLng] = useState(String(station.droneStationLongitude ?? ''))

    useEffect(() => {
        setEditLat(String(station.droneStationLatitude ?? ''))
        setEditLng(String(station.droneStationLongitude ?? ''))
    }, [station])

    useEffect(() => {
        if (!expanded) {
            setIsEditing(false)
        }
    }, [expanded])

    function startEdit() {
        setIsEditing(true)
    }

    function cancelEdit() {
        setIsEditing(false)
        setEditLat(String(station.droneStationLatitude ?? ''))
        setEditLng(String(station.droneStationLongitude ?? ''))
    }

    function saveEdit() {
        const parsedLat = parseFloat(editLat.replace(',', '.'))
        const parsedLng = parseFloat(editLng.replace(',', '.'))

        const payload: DroneStationEditPayload = {
            latitude: Number.isFinite(parsedLat)
                ? parsedLat
                : station.droneStationLatitude ?? 0,
            longitude: Number.isFinite(parsedLng)
                ? parsedLng
                : station.droneStationLongitude ?? 0,
        }

        setIsEditing(false)
        void onEdit(station, payload)
    }

    return (
        <div className="flex flex-col gap-5">
            <div
                className={
                    'rounded-[20px] overflow-hidden bg-[#EEF3F8] ' +
                    (expanded ? 'border-2 border-[#0063A3]' : 'border border-[#DCE9F5]')
                }
            >
                <button
                    type="button"
                    onClick={() => onToggle(station)}
                    className="flex w-full items-center gap-4 px-6 py-5 text-left"
                >
                    <div className="text-base">{station.name || '-'}</div>
                    <div className="ml-auto flex items-center gap-4">
                        <span
                            className={
                                'rounded-full border px-4 py-1 text-base font-medium ' +
                                (station.deviceOnlineStatus
                                    ? 'border-[#A3BA00] bg-[#DBE870] text-[#202020]'
                                    : 'border-[#B0B0B0] bg-[#D2D2D2] text-[#262626]')
                            }
                        >
                            {station.deviceOnlineStatus ? 'Online' : 'Offline'}
                        </span>
                        <span
                            className={
                                'inline-flex w-6 justify-center text-base text-[#555] transition-transform duration-200 ' +
                                (expanded ? 'rotate-90' : 'rotate-0')
                            }
                        >
                            ›
                        </span>
                    </div>
                </button>

                {expanded && (
                    <div className="border-t border-[#D0D9E2] bg-[#ffffff] px-6 py-6 text-base text-[#3F3F3F]">
                        <div className="flex flex-col">
                            <div className="mb-4 grid grid-cols-2 gap-x-10 gap-y-2 text-[#666666]">
                                <div className="flex gap-1">
                                    <span className="w-20 text-base leading-none">Call Sign</span>
                                    <span className="text-base font-semibold leading-none">{station.callsign || '-'}</span>
                                </div>
                                <div className="flex gap-1">
                                    <span className="w-16 text-base leading-none">Key</span>
                                    <span className="text-base font-semibold leading-none">{station.stationModel?.key || '-'}</span>
                                </div>

                                <div className="flex gap-1">
                                    <span className="w-20 text-base leading-none">Modell</span>
                                    <span className="text-base font-semibold leading-none">{station.stationModel?.name || '-'}</span>
                                </div>
                                <div className="flex gap-1">
                                    <span className="w-20 text-base leading-none">Mode</span>
                                    <span className="text-base font-semibold leading-none">{station.modeCode ?? '-'}</span>
                                </div>

                                <div className="flex gap-1">
                                    <span className="w-20 text-base leading-none">Seriennr.</span>
                                    <span className="text-base font-semibold leading-none">{station.serialNumber || '-'}</span>
                                </div>
                                <div className="flex gap-1">
                                    <span className="w-20 text-base leading-none">Klasse</span>
                                    <span className="text-base font-semibold leading-none">{station.stationModel?.deviceClass || '-'}</span>
                                </div>
                            </div>
                            <div className='pb-2'>
                                <div className="flex gap-1 text-[#666666]">
                                    <span className="w-20 text-base leading-none">
                                        Kamera
                                    </span>
                                    <span className="text-base font-semibold leading-none">
                                        {station.cameras?.length
                                            ? station.cameras.map((camera) => `${camera.cameraPosition} - ${camera.cameraIndex}`).join(', ')
                                            : '-'}                                </span>
                                </div>
                            </div>
                        </div>

                        <div className="my-6 border-t border-[#8E8E8E]" />

                        <div className='flex gap-1'>
                            <div className='w-20'><DrohneIcon /></div>
                            <div className="font-normal text-base text-[#666666]">
                                <span className='font-semibold'>{station.name ?? '-'}</span>
                                <span>{' — '}{station.droneName ?? '-'}</span>
                            </div>
                        </div>

                        <div className="my-6 border-t border-[#8E8E8E]" />

                        {isEditing ? (
                            <>
                                <div className='mb-4'>
                                    <p className="mb-3 font-semibold text-[#333333]">Name</p>
                                    <p className="text-[#333333]">{station.name || '-'}</p>
                                </div>
                                <div className='grid grid-cols-2 gap-x-6 gap-y-2 mb-4'>
                                    <Input
                                        label="Latitude"
                                        type="number"
                                        value={editLat}
                                        step={0.0001}
                                        onChange={setEditLat}
                                    />
                                    <Input
                                        label="Longitude"
                                        type="number"
                                        value={editLng}
                                        step={0.0001}
                                        onChange={setEditLng}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='mb-4'>
                                    <p className="mb-3 font-semibold text-[#333333]">
                                        Name
                                    </p>
                                    <p className="text-[#333333]">{station.name}</p>
                                </div>
                                <div className='grid grid-cols-2 gap-x-10 gap-y-2 mb-4'>
                                    <p className="mb-3 font-semibold text-[#333333]">
                                        Latitude
                                    </p>
                                    <p className="mb-3 font-semibold text-[#333333]">
                                        Longitude
                                    </p>
                                    <p className="text-[#333333]">{formatCoord(station.droneStationLatitude)}</p>
                                    <p className="text-[#333333]">{formatCoord(station.droneStationLongitude)}</p>

                                </div>
                            </>
                        )}
                    </div>

                )}
            </div>


            {expanded && !isEditing && (
                <div className="flex justify-end gap-5 px-2">
                    <Button
                        className="border border-[#E1320F] text-[#E1320F] gap-2"
                        onClick={() => onDelete(station)}
                    >
                        <TrashIcon />
                        Löschen
                    </Button>
                    <Button
                        className="h-12 rounded-full bg-[#E1320F] px-10 text-sm font-semibold text-white"
                        onClick={startEdit}
                        primary
                    >
                        Bearbeiten
                    </Button>
                </div>
            )}

            {expanded && isEditing && (
                <div className="flex justify-end gap-5 px-2">
                    <Button
                        className="h-12 rounded-full border border-[#E1320F] px-8 text-sm font-semibold text-[#E1320F]"
                        onClick={cancelEdit}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        className="h-12 rounded-full bg-[#E1320F] px-10 text-sm font-semibold text-white"
                        onClick={saveEdit}
                        primary
                    >
                        Speichern
                    </Button>
                </div>
            )}
        </div>

    )
}
